import React, { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ActivitySeriesBucket,
  AnalyticsBreakdown,
  AnalyticsGranularity,
  SegmentBucket,
} from "../../api/analyticsAdmin";
import {
  formatSegmentLabel,
  normalizeSeriesToChartData,
  resolveSeriesKeys,
} from "../../helpers/analyticsPlus";
import { formatInteger } from "../../helpers/formatNumber";
import { buildColorMap, colorFor, GranularityToggle, renderPieLabel } from "./activityCharts";

interface BreakdownRowProps {
  breakdown: AnalyticsBreakdown;
  title: string;
  series: ActivitySeriesBucket[];
  segmentBuckets: SegmentBucket[];
  granularity: AnalyticsGranularity;
  onGranularityChange: (granularity: AnalyticsGranularity) => void;
}

// One breakdown shown as a stacked bar chart over time (left) plus a
// composition pie (right). Presentational: data comes from the parent's single
// dashboard fetch.
const BreakdownRow: React.FC<BreakdownRowProps> = ({
  breakdown,
  title,
  series,
  segmentBuckets,
  granularity,
  onGranularityChange,
}) => {
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());

  const toggleKey = (key: string) =>
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const keys = resolveSeriesKeys(breakdown, series);
  const chartData = normalizeSeriesToChartData(series, keys);
  // Shared value -> colour map so the bar series and the pie slices agree on a
  // colour per segment even though they iterate their data in different orders.
  const colorMap = buildColorMap(keys);
  // Pie legend/tooltip read `value` via nameKey; relabel it for display while
  // the original bucket.value still drives colouring below.
  const pieData = segmentBuckets.map((bucket) => ({
    ...bucket,
    value: formatSegmentLabel(breakdown, bucket.value),
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="bg-white rounded-lg shadow p-6 lg:col-span-2">
        <div className="flex flex-wrap justify-between gap-4 mb-6">
          <h3 className="text-lg font-medium">{title}</h3>
          <GranularityToggle value={granularity} onChange={onGranularityChange} />
        </div>
        <div className="h-[320px]">
          {series.length === 0 ? (
            <div className="text-sm text-gray-500">No data for this range.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucketStart" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend
                  onClick={(o) =>
                    toggleKey(
                      String(
                        (o as { dataKey?: unknown; value?: string }).dataKey ??
                          (o as { value?: string }).value
                      )
                    )
                  }
                />
                {keys.map((key, index) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    stackId="activity"
                    name={formatSegmentLabel(breakdown, key)}
                    fill={colorMap.get(key) ?? colorFor(key, index)}
                    hide={hiddenKeys.has(key)}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium mb-1 text-center">Share of unique visitors</h3>
        <p className="text-xs text-gray-400 mb-4 text-center">Whole range, de-duplicated</p>
        {segmentBuckets.length === 0 ? (
          <div className="text-sm text-gray-500">No data for this range.</div>
        ) : (
          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="activeUsers"
                  nameKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  isAnimationActive={false}
                  label={renderPieLabel}
                  labelLine={false}
                >
                  {segmentBuckets.map((bucket, index) => (
                    <Cell
                      key={bucket.value}
                      fill={colorMap.get(bucket.value) ?? colorFor(bucket.value, index)}
                    />
                  ))}
                </Pie>
                <Legend />
                <Tooltip formatter={(value: number) => formatInteger(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default BreakdownRow;
